import axios from "axios";
import { API_BASE_URL } from "./config";

const PAGES_BASE_URL = `${API_BASE_URL}/pages`;

// Create optimized axios instance for faster requests
const apiClient = axios.create({
    timeout: 6000, // 6 seconds - balanced for various connection speeds
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
    // Max redirects
    maxRedirects: 5,
    // Validate status
    validateStatus: (status) => status >= 200 && status < 300,
});

// Request deduplication - prevent duplicate simultaneous requests
const pendingRequests = new Map();

/**
 * Fetch page by slug using dynamic routing (same pattern as blogs)
 * Optimized for faster response times
 * 
 * @param {string} slug - The page slug (e.g., "about", "contact-us", "home-page")
 * @returns {Promise<Object|null>} Page data or null
 */
export const fetchPageByDynamicRouting = async (slug) => {
    if (!slug || typeof slug !== "string") {
        throw new Error("fetchPageByDynamicRouting requires a valid slug");
    }

    // Check if there's already a pending request for this slug
    const requestKey = slug;
    if (pendingRequests.has(requestKey)) {
        // Return the existing promise instead of making a new request
        return pendingRequests.get(requestKey);
    }

    // Create the request promise
    const requestPromise = (async () => {
        try {
            const response = await apiClient.get(PAGES_BASE_URL, {
                params: {
                    "dynamic-page-routing": slug,
                },
            });

            return response?.data || null;
        } catch (error) {
            // Provide more specific error messages
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                throw new Error('Request timeout - server is taking too long to respond');
            }
            if (error.response) {
                // Server responded with error status
                throw error;
            } else if (error.request) {
                // Request was made but no response received
                throw new Error('No response from server - please check your connection');
            }
            throw error;
        } finally {
            // Remove from pending requests when done
            pendingRequests.delete(requestKey);
        }
    })();

    // Store the pending request
    pendingRequests.set(requestKey, requestPromise);

    return requestPromise;
};

/**
 * Legacy function - kept for backward compatibility if needed
 * @deprecated Use fetchPageByDynamicRouting instead
 */
export const fetchPageBySlug = async (slug) => {
    return fetchPageByDynamicRouting(slug);
};

/**
 * Compatibility function for PageResolver
 * @deprecated Use fetchPageByDynamicRouting instead
 */
export const findPageBySlug = async (slug) => {
    // This function is kept for compatibility but directly returns the page data
    // In the old implementation, this would return page info with api_url
    // Now we just return the full page data structure
    const pageData = await fetchPageByDynamicRouting(slug);
    if (pageData) {
        // Return in the format expected by old code
        return {
            api_url: `${PAGES_BASE_URL}?dynamic-page-routing=${slug}`,
            ...pageData
        };
    }
    return null;
};

/**
 * Compatibility function for PageResolver
 * @deprecated Use fetchPageByDynamicRouting instead
 */
export const fetchPageByApiUrl = async (apiUrl) => {
    // Extract slug from api_url if it contains the dynamic routing parameter
    const url = new URL(apiUrl);
    const slug = url.searchParams.get('dynamic-page-routing');
    if (slug) {
        return fetchPageByDynamicRouting(slug);
    }
    // Fallback: try to fetch directly from the URL
    try {
        const response = await apiClient.get(apiUrl);
        return response?.data || null;
    } catch (error) {
        throw error;
    }
};
